import { ChatMediaView } from 'components';
import Icon from 'components/Icon';
import Text from 'components/Text';
import Markdown from 'markdown-to-jsx';
import moment from 'moment';
import 'moment/locale/ja';
import { memo, useMemo } from 'react';
import { EContentSectionType, EUrlButtonType, TMessageDetail } from 'types';
import { getChatDocumentPresentation } from 'utils/chatDocument';
import getFileTypeFromUrl from 'utils/getFileTypeFromUrl';
import { toSafeHttpUrl } from 'utils/safeUrl';
import { TEXT_STRING } from '../../../constants';
import type { TLightboxMedia } from '../../MediaLightbox';

type Props = {
  message: TMessageDetail;
  isMyMessage?: boolean;
  onLoad?: () => void;
  onDeleteRequest?: (timetoken: string) => void;
  onMediaClick?: (media: TLightboxMedia) => void;
};

const MessageItem = ({
  message,
  isMyMessage,
  onLoad,
  onDeleteRequest,
  onMediaClick,
}: Props) => {
  // Memo, callbacks
  const [
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _messageData,
    text,
    media,
    actionLabel,
    actionUrl,
    isVideoUrl,
  ] = useMemo(() => {
    const messageChat = message.message?.message || message.message;
    if ((messageChat as { event?: string })?.event === 'RECALL_MESSAGE') {
      return [undefined, '', undefined, undefined, undefined, false];
    }
    const data = messageChat?.data;
    const mediaList = messageChat?.media;

    return [
      data,
      messageChat?.text?.text || messageChat?.text || '',
      mediaList,
      mediaList?.[0]?.actionLabel,
      mediaList?.[0]?.actionUrl,
      data?.type === EUrlButtonType.VIDEO_URL ||
        (mediaList &&
          mediaList.length > 0 &&
          mediaList[0].type?.includes(EContentSectionType.VIDEO) &&
          data?.url),
    ];
  }, [message]);

  const messageTime = useMemo(
    () => moment(+message.timetoken / 10000).format('HH:mm'),
    [message.timetoken],
  );

  const handleVideoUrlEnter = () => {
    if (actionUrl) {
      window.open(actionUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const messageChat = message.message?.message || message.message;
  if ((messageChat as { event?: string })?.event === 'RECALL_MESSAGE') {
    return null;
  }

  // PDF / ZIP は画像・動画と違い専用のファイルカードで描画する
  const documentView = media?.length
    ? getChatDocumentPresentation(media[0].type)
    : undefined;
  // URL はメッセージペイロード由来で内容を保証できないため、http(s) のみ通す
  const documentUrl = documentView ? toSafeHttpUrl(media[0].url) : undefined;

  return (
    <div
      className={`flex group/msg ${
        isMyMessage ? 'items-end justify-end' : 'items-start'
      } break-all`}
    >
      {isMyMessage && onDeleteRequest && (
        <button
          type="button"
          onClick={() => onDeleteRequest(String(message.timetoken))}
          className="flex items-center shrink-0 self-center mr-1 opacity-0 group-hover/msg:opacity-100 transition-opacity text-neutral-400 hover:text-red-500"
          aria-label={TEXT_STRING.MESSAGE.RECALL}
        >
          <Icon icon="close" width={14} height={14} />
        </button>
      )}
      {isMyMessage && (
        <Text fontSize={10} className="mr-3 mt-auto">
          {messageTime}
        </Text>
      )}

      {/* Message content */}
      <div
        className={`${
          isMyMessage ? 'bg-primary-200' : 'bg-transparent ml-3'
        } rounded-xl border border-neutral-200 max-w-[70%]`}
      >
        {text && (
          <div className="px-3 py-2 text-neutral-600 whitespace-pre-line">
            <Markdown
              options={{
                wrapper: 'p',
                forceInline: true,
                overrides: {
                  a: {
                    component: (props) => (
                      <a
                        href={props.href}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {props.children}
                      </a>
                    ),
                  },
                },
              }}
            >
              {text}
            </Markdown>
          </div>
        )}

        {documentView ? (
          documentUrl ? (
            <button
              type="button"
              onClick={() =>
                window.open(documentUrl, '_blank', 'noopener,noreferrer')
              }
              className={`flex items-center gap-3 px-3 py-2 w-full text-left hover:opacity-90 ${
                text ? '' : 'rounded-t-xl'
              } rounded-b-xl`}
            >
              <span className="shrink-0" data-testid="document-card-icon">
                <Icon icon={documentView.kind} width={28} height={28} />
              </span>
              <span className="min-w-0">
                <span className="block text-neutral-600 font-medium truncate">
                  {media[0].fileName || documentView.fileName}
                </span>
                <span className="block text-neutral-400 text-xs">
                  {documentView.openHint}
                </span>
              </span>
            </button>
          ) : (
            // URL が http(s) でない場合は開かせないが、無言で消すと気づけないため理由を出す
            <div
              className={`flex items-center gap-3 px-3 py-2 w-full ${
                text ? '' : 'rounded-t-xl'
              } rounded-b-xl`}
            >
              <span className="shrink-0">
                <Icon icon={documentView.kind} width={28} height={28} />
              </span>
              <span className="text-neutral-400 text-sm">
                {documentView.unavailable}
              </span>
            </div>
          )
        ) : actionLabel &&
          actionUrl &&
          media &&
          media.length > 0 &&
          media[0].type?.includes(EContentSectionType.IMAGE) ? (
          <div
            className={`relative ${
              text ? '' : 'rounded-t-xl'
            } cursor-pointer hover:opacity-90`}
            onClick={() => {
              if (actionUrl) {
                window.open(actionUrl, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            <img
              src={media[0].url}
              alt=""
              className={`max-w-full ${
                text ? 'rounded-lg' : 'rounded-t-xl rounded-b-lg'
              }`}
              onLoad={onLoad}
            />
            <div className="absolute bottom-6 left-0 right-0 flex justify-center px-0">
              <div className="bg-black bg-opacity-40 text-white text-base px-6 py-3 rounded-3xl font-medium shadow-lg text-center whitespace-nowrap">
                {actionLabel}
              </div>
            </div>
          </div>
        ) : isVideoUrl &&
          media &&
          media.length > 0 &&
          media[0].type?.includes(EContentSectionType.VIDEO) &&
          actionUrl ? (
          <div className={`relative ${text ? '' : 'rounded-t-xl'}`}>
            <video
              data-video-id={message.timetoken}
              data-testid="action-video"
              src={media[0].url}
              controls
              className={`max-w-full ${
                text ? 'rounded-lg' : 'rounded-t-xl rounded-b-lg'
              }`}
              onLoad={onLoad}
            />
            <button
              onClick={handleVideoUrlEnter}
              className="absolute top-3 right-3 bg-neutral-200 hover:bg-neutral-300 text-neutral-700 px-3 py-1.5 rounded-lg text-sm font-medium shadow-lg z-10"
            >
              Enter
            </button>
          </div>
        ) : media ? (
          <div className="relative">
            <div
              className={
                actionUrl && media[0]?.type?.includes('image')
                  ? 'cursor-pointer hover:opacity-90'
                  : ''
              }
              onClick={() => {
                if (actionUrl && media[0]?.type?.includes('image')) {
                  window.open(actionUrl, '_blank', 'noopener,noreferrer');
                }
              }}
            >
              <ChatMediaView
                listMedia={media}
                roundedTop={text ? '' : 'rounded-t-xl'}
                onLoad={onLoad}
                onMediaClick={actionUrl ? undefined : onMediaClick}
              />
            </div>

            {actionUrl && media[0]?.type?.includes('video') && (
              <div className="absolute right-1 flex justify-center px-0 z-10 top-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(actionUrl, '_blank', 'noopener,noreferrer');
                  }}
                  className="bg-black bg-opacity-40 text-white text-base px-3 py-1 rounded-3xl font-medium shadow-lg text-center whitespace-nowrap hover:bg-opacity-50 transition-opacity max-w-[200px] truncate"
                  title={
                    actionLabel?.trim() ||
                    TEXT_STRING.MESSAGE.ACTION_LABEL_DEFAULT
                  }
                >
                  {actionLabel?.trim() ||
                    TEXT_STRING.MESSAGE.ACTION_LABEL_DEFAULT}
                </button>
              </div>
            )}
          </div>
        ) : null}
        {message.url && !actionLabel && !isVideoUrl && (
          <ChatMediaView
            listMedia={[
              {
                url: message.url,
                type: getFileTypeFromUrl(message.url),
              },
            ]}
            roundedTop={text ? '' : 'rounded-t-xl'}
            onLoad={onLoad}
            onMediaClick={onMediaClick}
          />
        )}
      </div>

      {!isMyMessage && (
        <Text fontSize={10} className="ml-3 mt-auto">
          {messageTime}
        </Text>
      )}
    </div>
  );
};

// リスト全体がライトボックス開閉などで再レンダーされても、props が変わらない
// メッセージの再描画を避けるためメモ化する
export default memo(MessageItem);
